import { ChannelType } from 'cio/models/newsletter';
import { Measure } from 'cio/utils/metrics/common';

interface ChannelInfo {
  canAutoSendWinner: boolean;
  canSplit: boolean;
  icon: string;
  name: string;
  filterName: string;
  metrics: Measure[];
  requiresSubject: boolean;
  settingsPath: string;
  trackingAvailable: boolean;
  featureFlag?: string;
  beta?: boolean;
}

export interface Channel {
  type: ChannelType;
  enabled?: boolean;
  info: ChannelInfo;
}

const DEFAULT: ChannelInfo = {
  canAutoSendWinner: false,
  canSplit: false,
  icon: 'newsletter',
  name: 'unknown',
  filterName: 'unknown',
  metrics: ['failed', 'sent', 'opened', 'clicked', 'converted'],
  requiresSubject: false,
  settingsPath: 'workspace-settings',
  trackingAvailable: false,
};

export const CHANNEL_INFO: Record<ChannelType, ChannelInfo> = {
  email: {
    canAutoSendWinner: true,
    canSplit: true,
    icon: 'email',
    name: 'email',
    filterName: 'Email',
    metrics: [
      'failed',
      'sent',
      'delivered',
      'opened',
      'clicked',
      'bounced',
      'converted',
      'suppressed',
      'spammed',
      'unsubscribed',
      'clicked_to_opened',
      'topic_unsubscribed',
    ],
    requiresSubject: true,
    settingsPath: 'actions.email',
    trackingAvailable: true,
  },
  push: {
    canAutoSendWinner: false,
    canSplit: true,
    icon: 'mobile',
    name: 'push',
    filterName: 'Push',
    metrics: [
      'failed',
      'sent',
      'opened',
      'delivered',
      'undeliverable',
      'bounced',
      'converted',
      'suppressed',
      'topic_unsubscribed',
    ],
    requiresSubject: false,
    settingsPath: 'actions.push',
    trackingAvailable: false,
  },
  twilio: {
    canAutoSendWinner: false,
    canSplit: true,
    icon: 'sms',
    name: 'SMS',
    filterName: 'Twilio SMS',
    metrics: ['failed', 'sent', 'clicked', 'bounced', 'converted'],
    requiresSubject: false,
    settingsPath: 'actions.twilio',
    trackingAvailable: false,
  },
  in_app: {
    canAutoSendWinner: false,
    canSplit: true,
    icon: 'in-app',
    name: 'in-app',
    filterName: 'In-App',
    metrics: ['failed', 'sent', 'opened', 'clicked', 'converted'],
    requiresSubject: false,
    settingsPath: 'actions.in_app',
    trackingAvailable: false,
  },
  webhook: {
    canAutoSendWinner: false,
    canSplit: false,
    icon: 'webhook',
    name: 'webhook',
    filterName: 'Webhook',
    metrics: ['failed', 'sent', 'converted'],
    requiresSubject: false,
    settingsPath: 'actions.webhook',
    trackingAvailable: false,
  },
  unset: DEFAULT,
};

export function channelItem(channelType: ChannelType): Channel {
  return {
    type: channelType,
    info: channelInfo(channelType),
  };
}

export const CHANNELS = Object.keys(CHANNEL_INFO)
  .filter((channelType) => {
    return channelType !== 'unset';
  })
  .map(channelItem);

export default function channelInfo(type: ChannelType) {
  return CHANNEL_INFO[type] ?? DEFAULT;
}
